import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { ONBOARDING } from "../../constants/testIds";

const MARGIN = 16;

// UX-ONBOARD-01: rAF-driven measurement loop (not a one-shot getBoundingClientRect) so the
// spotlight tracks its target through sidebar entrance animations, window resizes and scroll —
// cheap since it only ever runs while the tour is actually open (a handful of steps, never
// looping in the background).
function useTrackedRect(testId, isOpen) {
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  useLayoutEffect(() => {
    if (!isOpen) return;
    if (!testId) { setRect(null); setMissing(false); return; }
    let raf;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el) { setMissing(true); setRect(null); }
      else { setMissing(false); setRect(el.getBoundingClientRect()); }
      raf = requestAnimationFrame(measure);
    };
    measure();
    return () => cancelAnimationFrame(raf);
  }, [testId, isOpen]);
  return { rect, missing };
}

function clamp(v, min, max) { return Math.min(Math.max(v, min), Math.max(min, max)); }

// Prefers right-of-target (sidebars) -> below (top nav bars) -> above (bottom tab bars) -> center.
function computeTooltipPos(rect, size) {
  const vw = window.innerWidth, vh = window.innerHeight;
  if (!rect) return { top: vh / 2 - size.h / 2, left: vw / 2 - size.w / 2 };
  if (rect.right + MARGIN + size.w < vw) {
    return { top: clamp(rect.top, MARGIN, vh - size.h - MARGIN), left: rect.right + MARGIN };
  }
  if (rect.bottom + MARGIN + size.h < vh) {
    return { top: rect.bottom + MARGIN, left: clamp(rect.left, MARGIN, vw - size.w - MARGIN) };
  }
  if (rect.top - MARGIN - size.h > 0) {
    return { top: rect.top - MARGIN - size.h, left: clamp(rect.left, MARGIN, vw - size.w - MARGIN) };
  }
  return { top: vh / 2 - size.h / 2, left: vw / 2 - size.w / 2 };
}

// UX-ONBOARD-01 — the black semi-transparent spotlight overlay itself. `steps[stepIndex]` with
// `target: null` renders as a centered welcome card (no cutout); every other step punches a
// rounded "hole" over the live nav element via the box-shadow trick (a single div sized to the
// target's rect with `box-shadow: 0 0 0 9999px rgba(0,0,0,.78)` — everything outside that div IS
// the dim layer, no SVG mask needed) and points a tooltip at it.
export function OnboardingTourOverlay({ steps, tour }) {
  const { isOpen, stepIndex, close, goTo } = tour;
  const reducedMotion = useReducedMotion();
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ w: 320, h: 200 });
  const step = steps[stepIndex];
  const { rect, missing } = useTrackedRect(step?.target, isOpen);

  // Honest gap handling: a role/module-gated nav item that isn't in the DOM this login gets
  // skipped automatically rather than spotlighting nothing.
  useLayoutEffect(() => {
    if (isOpen && step?.target && missing) goTo(stepIndex + 1, steps.length);
  }, [isOpen, step, missing, stepIndex, goTo, steps.length]);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const r = tooltipRef.current.getBoundingClientRect();
      if (r.width && r.height && (r.width !== tooltipSize.w || r.height !== tooltipSize.h)) {
        setTooltipSize({ w: r.width, h: r.height });
      }
    }
  }, [stepIndex, rect, tooltipSize.w, tooltipSize.h]);

  if (!isOpen || !step) return null;

  const pos = computeTooltipPos(rect, tooltipSize);
  const spot = rect ? { top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 } : null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid={ONBOARDING.overlay}
        key="onboarding-overlay"
        initial={reducedMotion ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={reducedMotion ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.18 }}
        style={{ position: "fixed", inset: 0, zIndex: 10000 }}
        onKeyDown={(e) => e.key === "Escape" && close()}
      >
        {spot ? (
          <motion.div
            layout={!reducedMotion}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "fixed", top: spot.top, left: spot.left, width: spot.width, height: spot.height,
              borderRadius: 12, boxShadow: "0 0 0 9999px rgba(0,0,0,0.78)",
              border: "2px solid var(--scarlet-accent)", pointerEvents: "none",
            }}
          />
        ) : (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)" }} />
        )}

        <motion.div
          ref={tooltipRef}
          data-testid={ONBOARDING.tooltip}
          initial={reducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "fixed", top: pos.top, left: pos.left, width: 320, maxWidth: "90vw",
            background: "#141416", color: "#fff", borderRadius: 14, padding: "20px 20px 16px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
          }}
        >
          <button
            data-testid={ONBOARDING.closeButton}
            onClick={close}
            className="absolute top-3 right-3 opacity-60 hover:opacity-100 transition-opacity"
            style={{ color: "#fff" }}
            aria-label="Close tour"
          >
            <X size={16} />
          </button>
          <p data-testid={ONBOARDING.stepIndicator} className="text-xs font-semibold mb-1.5" style={{ color: "var(--scarlet-accent)" }}>
            Step {stepIndex + 1} of {steps.length}
          </p>
          <h3 className="text-base font-bold mb-1.5 pr-4">{step.title}</h3>
          <p className="text-sm leading-relaxed opacity-80 mb-4">{step.body}</p>
          <div className="flex items-center justify-between">
            <button data-testid={ONBOARDING.skipButton} onClick={close} className="text-xs font-medium opacity-60 hover:opacity-100 transition-opacity">
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {stepIndex > 0 && (
                <button
                  data-testid={ONBOARDING.backButton}
                  onClick={() => goTo(stepIndex - 1, steps.length)}
                  className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: "rgba(255,255,255,0.1)" }}
                >
                  <ArrowLeft size={12} /> Back
                </button>
              )}
              <button
                data-testid={ONBOARDING.nextButton}
                onClick={() => goTo(stepIndex + 1, steps.length)}
                className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full text-white"
                style={{ background: "var(--scarlet-accent)" }}
              >
                {stepIndex === steps.length - 1 ? "Got it" : "Next"} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

// Persistent floating help icon — the ONLY way to manually re-trigger the tour once the
// first-3-4-logins auto-show window has passed. Same fixed bottom-right placement in every
// shell unless a caller needs to sit it above its own fixed bottom bar (Member app's tab bar).
export function OnboardingHelpButton({ onClick, style }) {
  return (
    <button
      data-testid={ONBOARDING.helpButton}
      onClick={onClick}
      aria-label="Show onboarding tour"
      className="fixed flex items-center justify-center w-11 h-11 rounded-full text-white transition-transform hover:scale-110 active:scale-95"
      style={{ bottom: 24, right: 24, background: "var(--scarlet-accent)", boxShadow: "0 8px 20px rgba(0,0,0,0.35)", zIndex: 9998, ...style }}
    >
      <span className="font-bold text-base leading-none">?</span>
    </button>
  );
}
